import * as React from 'react'
import { SlideOver } from '@/components/ui/slide-over'
import { ErrorSummary } from '@/components/visitone/error-summary'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { SaveBar, type SaveState } from '@/components/visitone/save-bar'
import { useRegisterUnsavedChanges, useUnsavedChanges } from '@/components/visitone/unsaved-changes'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { usePeopleMutations, useRoles } from '@/hooks/use-people'
import { ORGANISATION_ADMIN, type Person } from '@/lib/bff/people'
import { RoleChoices, type RoleChoice } from '@/components/visitone/surfaces/people/people-shared'
import { builtInDescription, changedCount, errorCode, errorStatus, personName } from '@/lib/people/people-text'

const FIELD_ID = 'change-roles'

type Problem = 'noRole' | 'changedMeanwhile' | 'refused' | null

/*
 * Change roles (VIS-332): another person's whole role list, saved with the
 * panel's SaveBar (VIS-321). Roles the caller can't give or take away are
 * shown as text with the reason, never as disabled checkboxes, and keep
 * their current state when saved. Everyone keeps at least one role.
 */
export function ChangeRolesPanel({
  person,
  onClose,
  onSaved,
}: {
  person: Person | null
  onClose: () => void
  onSaved: (person: Person) => void
}) {
  const t = useT()
  const { tenantName: tenant } = useAccess()
  const { confirmThen } = useUnsavedChanges()
  const open = person !== null
  const roles = useRoles(open)
  const mutations = usePeopleMutations()
  const summaryRef = React.useRef<HTMLDivElement>(null)

  const initial = React.useMemo(() => person?.roles.map((role) => role.id) ?? [], [person])
  const [selected, setSelected] = React.useState<string[]>(initial)
  const [problem, setProblem] = React.useState<Problem>(null)
  const [saveState, setSaveState] = React.useState<SaveState>('idle')
  const [shownFor, setShownFor] = React.useState(person)
  if (person !== shownFor) {
    setShownFor(person)
    setSelected(initial)
    setProblem(null)
    setSaveState('idle')
  }

  const name = person ? personName(person) : ''
  const count = changedCount(initial, selected)
  const discard = React.useCallback(() => {
    setSelected(initial)
    setProblem(null)
    setSaveState('idle')
  }, [initial])
  useRegisterUnsavedChanges(open ? { count, subject: t('people.roles.subject', { name }), discard } : null)

  const choices: RoleChoice[] = (roles.roles ?? []).flatMap((role): RoleChoice[] => {
    const held = initial.includes(role.id)
    const description = builtInDescription(t, role.id, tenant)
    if (role.id === ORGANISATION_ADMIN && held && person?.onlyOrganisationAdmin) {
      return [{ role, locked: t('people.roles.lockedOnlyOrgAdmin', { name, tenant }) }]
    }
    if (!role.canAssign) {
      if (role.kind === 'builtIn') {
        return [{ role, locked: held ? t('people.roles.lockedHeld') : t('people.roles.lockedNotHeld') }]
      }
      return held ? [{ role, locked: t('people.roles.lockedBeyondYou') }] : []
    }
    return [{ role, description }]
  })

  const showNoRole = () => {
    setProblem('noRole')
    requestAnimationFrame(() => summaryRef.current?.focus())
  }

  const save = async () => {
    if (!person || saveState === 'saving') return
    if (selected.length === 0) return showNoRole()
    setProblem(null)
    setSaveState('saving')
    try {
      await mutations.changeRoles(person.userId, selected)
      setSaveState('idle')
      onSaved(person)
    } catch (error) {
      const code = errorCode(error)
      const status = errorStatus(error)
      if (code === 'no_role_left') {
        setSaveState('idle')
        showNoRole()
      } else if (status === 403) {
        setSaveState('idle')
        setProblem('refused')
      } else if (status === 404 || status === 409) {
        setSaveState('idle')
        setProblem('changedMeanwhile')
        void mutations.revalidate()
      } else {
        setSaveState('failed')
      }
    }
  }

  const toggle = (roleId: string, next: boolean) => {
    setSelected((current) => (next ? [...current, roleId] : current.filter((id) => id !== roleId)))
    if (problem === 'noRole') setProblem(null)
    if (saveState === 'failed') setSaveState('idle')
  }

  return (
    <SlideOver
      open={open}
      title={t('people.roles.panelTitle', { name })}
      onClose={onClose}
      footer={
        <SaveBar
          docked="panel"
          count={count}
          invalidCount={problem === 'noRole' ? 1 : 0}
          state={saveState}
          onDiscard={() => confirmThen({ kind: 'discard' }, discard)}
          onSave={() => void save()}
        />
      }
    >
      {person ? (
        <div className="flex flex-col gap-5">
          {person.name && person.email ? <p className="text-sm text-fg-muted wrap-anywhere">{person.email}</p> : null}
          {problem === 'noRole' ? (
            <ErrorSummary
              ref={summaryRef}
              errors={[
                {
                  key: FIELD_ID,
                  label: t('people.roles.legend', { tenant }),
                  message: t('people.roles.noneErrorField', { name, tenant }),
                },
              ]}
              onJump={() => document.querySelector<HTMLElement>(`#${FIELD_ID} input`)?.focus()}
            />
          ) : null}
          {problem === 'changedMeanwhile' ? (
            <OperationFeedback state="error" title={t('people.roles.changedMeanwhile', { name })} />
          ) : null}
          {problem === 'refused' ? (
            <OperationFeedback
              state="error"
              title={t('core.access.actionRefusedTitle')}
              detail={t('core.access.actionRefusedBody', { tenant })}
            />
          ) : null}
          {roles.error ? (
            <OperationFeedback
              state="error"
              title={t('people.rolesPage.loadFailed.title')}
              detail={t('people.rolesPage.loadFailed.body')}
              onRetry={() => void roles.revalidate()}
            />
          ) : !roles.roles ? (
            <p role="status" className="text-sm text-fg-muted">
              {t('people.rolesPage.loading')}
            </p>
          ) : (
            <RoleChoices
              id={FIELD_ID}
              legend={t('people.roles.legend', { tenant })}
              help={t('people.roles.help', { name })}
              error={problem === 'noRole' ? t('people.roles.noneError') : undefined}
              choices={choices}
              selected={selected}
              onToggle={toggle}
            />
          )}
        </div>
      ) : null}
    </SlideOver>
  )
}
